import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Bell,
    Loader2,
    RefreshCw,
    Circle,
    CheckCircle2,
    MapPinned,
    Phone,
    AlertTriangle,
    CheckCheck,
    FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortableHead } from "../components/SortableHead";

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};

const FIELD_LABELS = {
    email: "Email",
    cell_personale: "Cellulare",
    telefono_fisso: "Telefono fisso",
    indirizzo: "Indirizzo",
    cap: "CAP",
    citta: "Città",
    provincia: "Provincia",
};

export default function EmployeeChangesPage() {
    const [items, setItems] = useState([]);
    const [newCount, setNewCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [downloading, setDownloading] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = filter !== "all" ? { status: filter } : {};
            const { data } = await api.get("/employee-changes", { params });
            setItems(data.items || []);
            setNewCount(data.new_count || 0);
        } catch (e) {
            toast.error("Errore caricamento");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const toggleStatus = async (item) => {
        const next = item.status === "new" ? "read" : "new";
        const fd = new FormData();
        fd.append("status", next);
        try {
            await api.put(`/employee-changes/${item.id}/status`, fd);
            setItems((arr) => arr.map((i) => i.id === item.id ? { ...i, status: next } : i));
            toast.success(next === "read" ? "Segnato come letto" : "Riaperto");
            if (next === "read") setNewCount((c) => Math.max(0, c - 1));
            else setNewCount((c) => c + 1);
        } catch (e) {
            toast.error("Errore");
        }
    };

    const markAllRead = async () => {
        if (newCount === 0) return;
        if (!window.confirm(`Segnare come lette tutte le ${newCount} notifiche in sospeso?`)) return;
        try {
            const { data } = await api.post("/employee-changes/mark-all-read");
            toast.success(`${data.updated} notific${data.updated === 1 ? "a" : "he"} segnata${data.updated === 1 ? "" : "e"} come letta${data.updated === 1 ? "" : "e"}`);
            await fetchData();
        } catch (e) {
            toast.error("Errore");
        }
    };

    const downloadReport = async () => {
        setDownloading(true);
        try {
            const params = {};
            if (filter !== "all") params.status = filter;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            const res = await api.get("/employee-changes/report/pdf", { params, responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "Modifiche_dati_dipendenti.pdf";
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Report PDF generato");
        } catch (e) {
            toast.error("Errore generazione report");
        } finally {
            setDownloading(false);
        }
    };

    const { sortedItems: itemsSorted, sortKey, sortDir, requestSort } = useSortableTable(items, {
        customAccessors: {
            employee_label: (it) => (it.employee_label || "").toLowerCase(),
            tipologia: (it) => it.residence_changed ? "1-residenza" : "2-contatti",
        },
    });

    return (
        <div data-testid="employee-changes-page">
            <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="relative flex h-12 w-12 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                            <Bell className="h-6 w-6" />
                            {newCount > 0 && (
                                <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white" data-testid="notif-new-badge">
                                    {newCount}
                                </span>
                            )}
                        </div>
                        <div>
                            <h1 className="font-display text-2xl font-bold text-[#376DB3]">Modifiche dati dipendenti</h1>
                            <p className="text-sm text-slate-600">
                                Notifiche delle modifiche a contatti e residenza inviate dai dipendenti.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {newCount > 0 && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={markAllRead}
                                className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                data-testid="mark-all-read-btn"
                            >
                                <CheckCheck className="h-4 w-4" />
                                Segna tutte come lette ({newCount})
                            </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="gap-2" data-testid="refresh-btn">
                            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                            Aggiorna
                        </Button>
                    </div>
                </div>

                <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="h-9 w-56" data-testid="filter-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tutte le notifiche</SelectItem>
                            <SelectItem value="new">Da leggere ({newCount})</SelectItem>
                            <SelectItem value="read">Già lette</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="ml-auto flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Report</span>
                        <Input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="h-9 w-36 text-xs"
                            title="Dal giorno (opzionale)"
                            data-testid="report-date-from"
                        />
                        <span className="text-xs text-slate-400">→</span>
                        <Input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="h-9 w-36 text-xs"
                            title="Al giorno (opzionale)"
                            data-testid="report-date-to"
                        />
                        <Button
                            size="sm"
                            onClick={downloadReport}
                            disabled={downloading}
                            className="gap-2 bg-[#15498F] text-white hover:bg-[#15498F]/90"
                            data-testid="download-report-btn"
                        >
                            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                            Report PDF
                        </Button>
                    </div>
                </div>

                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                                <TableHead className="w-10"></TableHead>
                                <SortableHead sortKey="employee_label" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                    Dipendente
                                </SortableHead>
                                <TableHead>Modifiche</TableHead>
                                <SortableHead sortKey="created_at" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="hidden md:table-cell">
                                    Inviata il
                                </SortableHead>
                                <SortableHead sortKey="tipologia" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                    Tipologia
                                </SortableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={5} className="py-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></TableCell></TableRow>
                            ) : itemsSorted.length === 0 ? (
                                <TableRow><TableCell colSpan={5} className="py-12 text-center text-sm text-slate-500">
                                    <Bell className="mx-auto mb-2 h-10 w-10 text-slate-300" />
                                    Nessuna notifica.
                                </TableCell></TableRow>
                            ) : itemsSorted.map((item) => {
                                const isNew = item.status === "new";
                                return (
                                    <TableRow key={item.id} className={isNew ? "bg-blue-50/30" : ""} data-testid={`change-row-${item.id}`}>
                                        <TableCell>
                                            <button
                                                type="button"
                                                onClick={() => toggleStatus(item)}
                                                className="rounded p-1 hover:bg-slate-100"
                                                title={isNew ? "Segna come letto" : "Riapri"}
                                                data-testid={`toggle-status-${item.id}`}
                                            >
                                                {isNew ? (
                                                    <Circle className="h-4 w-4 fill-blue-500 text-blue-500" />
                                                ) : (
                                                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                                )}
                                            </button>
                                        </TableCell>
                                        <TableCell>
                                            <Link to={`/dipendenti/${item.employee_id}`} className="font-medium text-[#376DB3] hover:underline" data-testid={`emp-link-${item.id}`}>
                                                {item.employee_label || "—"}
                                            </Link>
                                            <div className="text-xs text-slate-500">matr. {item.matricola || "—"}</div>
                                        </TableCell>
                                        <TableCell>
                                            <ul className="space-y-1 text-sm">
                                                {(item.changes || []).map((c, idx) => (
                                                    <li key={`${c.field}-${idx}`} className="text-slate-700">
                                                        <span className="font-medium text-slate-600">{FIELD_LABELS[c.field] || c.field}:</span>{" "}
                                                        <span className="text-slate-400 line-through">{c.old || "—"}</span>
                                                        {" → "}
                                                        <span className="font-medium text-emerald-700">{c.new || "—"}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            {item.note && (
                                                <div className="mt-1 max-w-md text-xs italic text-slate-600">"{item.note}"</div>
                                            )}
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell font-mono-numbers text-xs text-slate-600">
                                            {fmtDateTime(item.created_at)}
                                            {item.status === "read" && item.reviewed_at && (
                                                <div className="text-[10px] text-emerald-600">letta il {fmtDateTime(item.reviewed_at)}</div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {item.residence_changed ? (
                                                <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-50 text-amber-700" data-testid={`badge-residence-${item.id}`}>
                                                    <MapPinned className="h-3 w-3" />
                                                    Cambio residenza
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="gap-1 border-slate-300 bg-slate-50 text-slate-600">
                                                    <Phone className="h-3 w-3" />
                                                    Contatti
                                                </Badge>
                                            )}
                                            {item.residence_changed && (
                                                <div className="mt-1 flex items-center gap-1 text-[10px] text-amber-700">
                                                    <AlertTriangle className="h-3 w-3" />
                                                    Verifica documento ufficiale
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
                <div className="mt-3 text-xs text-slate-500">{items.length} notifiche</div>
            </main>
        </div>
    );
}
